

<template>
  <v-container class="d-flex justify-center align-center">
      <v-card class="pa-6 rounded-lg shadow-lg " style="width: 400px;">
          <!-- Avatar Section -->
          <div class="d-flex justify-center mb-4">
              <AvatarComponent avatarSrc="@/assets/images/cute-avatar.png" />
          </div>

          <!-- Step 1: School Details -->
          <template v-if="currentStep === 1">
              <v-card-title class="text-h5 text-center mb-4">School Details</v-card-title>
              <SchoolDetailsForm :form="form" />
          </template>

          <!-- Step 2: User Details -->
          <template v-else-if="currentStep === 2">
              <v-card-title class="text-h5 text-center mb-4">User Details</v-card-title>
              <UserDetailsForm :form="form" />
          </template>

          <!-- Step Navigation Buttons -->
          <v-row justify="space-between" class="mt-4">
              <v-btn v-if="currentStep > 1" @click="prevStep" color="primary" outlined>
                  Previous
              </v-btn>
              <v-btn v-if="currentStep < totalSteps" @click="nextStep" color="primary">
                  Next
              </v-btn>
              <SubmitButton v-if="currentStep === totalSteps" :handleSignUp="handleSignUp" />
          </v-row>

          <!-- Snackbar for feedback -->
          <v-snackbar v-model="showFeedback" :color="feedbackColor" timeout="4000" right top absolute>
              <v-icon class="me-2">{{ feedbackIcon }}</v-icon>
              {{ feedback.message }}
          </v-snackbar>
      </v-card>
  </v-container>
</template>

<script setup>
import AvatarComponent from "@/components/Modules/Authentication/Register/AvatarComponent.vue";
import SchoolDetailsForm from "@/components/Modules/Authentication/Register/SchoolDetailsForm.vue";
import UserDetailsForm from "@/components/Modules/Authentication/Register/UserDetailsForm.vue";
import SubmitButton from "@/components/Modules/Authentication/Register/SubmitButton.vue";
import { ref, reactive, computed } from "vue";
import { register } from "@/services/apiServices.js";

const form = reactive({
  schoolName: "",
  schoolContact: "",
  schoolAddress: "",
  principalName: "",
  first_name: "",
  last_name: "",
  contact: "",
  email: "",
  password: "",
});

const feedback = reactive({ type: "", message: "" });
const showFeedback = ref(false);
const currentStep = ref(1);
const totalSteps = 3;

const nextStep = () => {
  let message = "";

  switch (currentStep.value) {
    case 1:
      if (!form.schoolName) message = "School Name is required.";
      else if (!form.schoolContact) message = "School Contact is required.";
      else if (!form.schoolAddress) message = "School Address is required.";
      else if (!form.principalName) message = "Principal Name is required.";
      break;
    case 2:
      if (!form.first_name) message = "First Name is required.";
      else if (!form.last_name) message = "Last Name is required.";
      else if (!form.contact) message = "Contact is required.";
      else if (!form.email) message = "Email is required.";
      else if (!form.password) message = "Password is required.";
      break;
  }

  if (message) {
    showFeedbackMessage("error", message);
  } else {
    showFeedbackMessage("success", "Step completed successfully!");
    currentStep.value++;
  }
};

const showFeedbackMessage = (type, message) => {
  feedback.type = type;
  feedback.message = message;
  showFeedback.value = true;
};

const feedbackColor = computed(() =>
  feedback.type === "success" ? "green" : "red"
);

const feedbackIcon = computed(() =>
  feedback.type === "success" ? "mdi-check-circle" : "mdi-alert-circle"
);

const prevStep = () => {
  currentStep.value--;
};

const validateStep = (step) => {
  switch (step) {
    case 1:
      return (
        form.schoolName &&
        form.schoolContact &&
        form.schoolAddress &&
        form.principalName
      );
    case 2:
      return (
        form.first_name &&
        form.last_name &&
        form.contact &&
        form.email &&
        form.password
      );
    case 3:
      return true; // Step 3 is assumed as the submit step
    default:
      return false;
  }
};

const handleSignUp = async () => {

  if (!validateStep(currentStep.value)) {
    feedback.value = { type: "error", message: "Form is not valid!" };
    return;
  }
  try {
    feedback.value = { type: "info", message: "Registering, please wait..." };
    const res = await register(form);
    // console.log("after ")
    if (res.first_login) {
      feedback.value = {
        type: "success",
        message: "Registration successful! 🎉",
      };
    } else {
      feedback.value = {
        type: "error",
        message: res.message || "Something went wrong during registration.",
      };
    }
  } catch {
    feedback.value = {
      type: "error",
      message: "An error occurred. Please try again later.",
    };
  }
};


</script>

<style scoped>
.v-card {
  max-width: 600px;
  margin: auto;
  border-radius: 15px;
}

.v-btn {
  transition: background-color 0.3s, transform 0.3s;
}

.v-btn:hover {
  background-color: #0056b3;
  transform: scale(1.05);
}

.v-snackbar {
  font-size: 14px;
}

.avatar {
  border-radius: 50%;
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
}

.v-card-title {
  font-weight: bold;
  color: #333;
}

.v-row {
  margin-top: 20px;
}
</style>